import { useMemo } from 'react';
import { Check } from 'lucide-react';
import type { SamplingPlanItem } from '@argos/api-types';
import { INPUT_INLINE_CLS, LABEL_CLS } from '../../styles/tokens';
import * as haptics from '../../lib/haptics';
import type { SampleCollected } from './state';

interface Props {
  /** All non-gauge-only plan items for this well — drives the group headers. */
  items: SamplingPlanItem[];
  /** Flat sample list (pre-expanded by container count). */
  samples: SampleCollected[];
  setSamples: (next: SampleCollected[]) => void;
}

/** Sample-collection step. For each analyte group at this well, list every
 *  container as a checkbox row with an optional lab-sample-ID field. The
 *  `filtered` flag is per-row so a tech can mark some containers filtered
 *  and others not (e.g. only the dissolved-metals subset). */
export function SamplesStep({ items, samples, setSamples }: Props) {
  // Group sample rows by analyte_group so we can render group headers
  // even when the underlying flat list is one row per container.
  const grouped = useMemo(() => {
    const map = new Map<string, { item: SamplingPlanItem; rows: Array<{ idx: number; row: SampleCollected }> }>();
    samples.forEach((row, idx) => {
      const key = row.analyte_group ?? '';
      if (!map.has(key)) {
        const item = items.find((i) => i.analyte_group === row.analyte_group) ?? items[0];
        map.set(key, { item, rows: [] });
      }
      map.get(key)!.rows.push({ idx, row });
    });
    return Array.from(map.entries()).map(([group, payload]) => ({ group, ...payload }));
  }, [samples, items]);

  function toggleRow(idx: number) {
    haptics.tap();
    const next = [...samples];
    next[idx] = { ...next[idx], collected: !next[idx].collected };
    setSamples(next);
  }
  function setLabId(idx: number, labId: string) {
    const next = [...samples];
    next[idx] = { ...next[idx], lab_id: labId };
    setSamples(next);
  }
  function toggleFiltered(idx: number) {
    const next = [...samples];
    next[idx] = { ...next[idx], filtered: !next[idx].filtered };
    setSamples(next);
  }

  const totalCollected = samples.filter((s) => s.collected).length;
  const totalRows = samples.length;

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-mono-100">Sample collection</h2>
        <p className="text-xs text-mono-400 mt-0.5">
          Check each container as it goes into the cooler. {totalCollected} / {totalRows} collected.
        </p>
      </header>

      <div className="space-y-3">
        {grouped.map(({ group, item, rows }) => {
          const selectedAnalytes = (item as unknown as { selected_analytes?: string[] | null }).selected_analytes;
          return (
            <section key={group || 'untitled'} className="rounded-lg border border-mono-800 bg-mono-900/40 p-3 space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-mono-100 truncate">
                  {group || 'Untitled group'}{item.method && <span className="text-mono-400 font-normal"> · {item.method}</span>}
                </h3>
                <span className="text-[10px] tracking-widest uppercase text-mono-500">
                  {rows.filter((r) => r.row.collected).length}/{rows.length}
                </span>
              </div>
              {selectedAnalytes && selectedAnalytes.length > 0 && (
                <div className="text-[11px] text-mono-400 italic">
                  Lab will report: {selectedAnalytes.join(', ')}
                </div>
              )}
              <ul className="space-y-1.5">
                {rows.map(({ idx, row }, i) => (
                  <li key={idx} className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleRow(idx)}
                      className={`min-h-touch min-w-touch flex items-center justify-center rounded-md border text-sm transition-colors ${row.collected ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200' : 'bg-mono-900 border-mono-700 text-mono-400 active:bg-mono-800'}`}
                      aria-pressed={row.collected}
                    >
                      {row.collected ? <Check className="h-5 w-5" strokeWidth={2.6} /> : <span className="h-5 w-5" aria-hidden />}
                    </button>
                    <span className="text-sm text-mono-200 flex-1 min-w-0 truncate">
                      <span className="text-mono-500">{i + 1}/{rows.length} ·</span> {row.container}
                      {row.preservative && <span className="text-mono-400"> · {row.preservative}</span>}
                    </span>
                    <label className="flex items-center gap-1 text-[11px] text-mono-400 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="accent-cyan-500"
                        checked={row.filtered}
                        onChange={() => toggleFiltered(idx)}
                      />
                      filtered
                    </label>
                    <input
                      type="text"
                      placeholder="lab ID"
                      value={row.lab_id ?? ''}
                      onChange={(e) => setLabId(idx, e.target.value)}
                      className={`${INPUT_INLINE_CLS} flex-1 min-w-[6rem] !w-auto`}
                    />
                  </li>
                ))}
              </ul>
              <p className={LABEL_CLS}>
                {item.hold_time_days ? `Lab hold: ${item.hold_time_days}d` : 'no lab hold set'}
                {(item as unknown as { low_flow?: boolean }).low_flow && ' · low-flow'}
              </p>
            </section>
          );
        })}
      </div>
    </div>
  );
}
